import { type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '~/utils/common'

/**
 * Text component types
 */
export type TextType = 'body' | 'heading' | 'display' | 'label' | 'link'

/**
 * Text component sizes
 */
export type TextSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'

/**
 * Text component weights
 */
export type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold' | 'light'

/**
 * Text component variants
 */
export type TextVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted'
  | 'dark'
  | 'light'
  | 'link'

/**
 * Text component props
 */
export type TextProps = HTMLAttributes<HTMLSpanElement> & {
  type?: TextType
  size?: TextSize
  weight?: TextWeight
  variant?: TextVariant
  className?: string
  children?: ReactNode
  as?: 'span' | 'p' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label' | 'a'
}

/**
 * Text component mapping for types
 */
const TEXT_TYPE_CLASSES: Record<TextType, string> = {
  body: '', // For regular paragraph text and general content - weight controlled by weight prop
  heading: 'tracking-tight', // For section titles and important content headers
  display: 'tracking-tighter', // For large, prominent text like page titles or hero sections
  label: '', // For form labels, captions, and smaller descriptive text - weight controlled by weight prop
  link: 'underline', // For interactive text that navigates to another screen or URL
}

/**
 * Text component mapping for sizes
 */
const TEXT_SIZE_CLASSES: Record<TextSize, string> = {
  '2xs': 'text-2xs',
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
}

/**
 * Text component mapping for weights
 */
const TEXT_WEIGHT_CLASSES: Record<TextWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  light: 'font-light',
}

/**
 * Text component mapping for variants
 */
const TEXT_VARIANT_CLASSES: Record<TextVariant, string> = {
  default: 'text-light',
  primary: 'text-primary-10',
  secondary: 'text-secondary',
  success: 'text-success-10',
  warning: 'text-warning-10',
  danger: 'text-danger-10',
  muted: 'text-light-20',
  dark: 'text-dark-20',
  light: 'text-light-10',
  link: 'text-blue-600',
}

/**
 * Enhanced Text component with Tailwind CSS styling
 *
 * @param props - Text component props
 * @returns React component
 */
export function Text({
  type = 'body',
  size = 'md',
  weight = 'normal',
  variant = 'default',
  className,
  as,
  style,
  ...rest
}: TextProps) {
  // Determine the HTML element to render
  // Default to semantic elements based on type, or use 'as' prop if provided
  const Component =
    as || (type === 'heading' ? 'h2' : type === 'label' ? 'label' : type === 'link' ? 'a' : 'span')

  // Combine all classes based on props
  const textClasses = cn(
    TEXT_TYPE_CLASSES[type],
    TEXT_SIZE_CLASSES[size],
    TEXT_VARIANT_CLASSES[variant],
    TEXT_WEIGHT_CLASSES[weight],
    className,
  )

  // Merge font family with any custom styles
  const mergedStyle = {
    fontFamily: 'SF Pro',
    ...style,
  }

  return <Component className={textClasses} style={mergedStyle} {...rest} />
}
